import React from 'react';
import {
  PROCESS_DEFINITIONS,
  canAccessStepGlobally,
  isProcessComplete,
} from '../../../shared/constants/flowConfig';
import { useWorkflowState } from '../../../shared/stores/useWorkflowStore';
import { useShallow } from 'zustand/shallow';

const DashboardWorkflowOverview = () => {
  const [stepCompletion, patientData, currentWorkflow] = useWorkflowState(
    useShallow((state) => [state.stepCompletion, state.patientData, state.currentWorkflow])
  );

  if (!patientData) return null;

  const activeProcessIndex = PROCESS_DEFINITIONS.findIndex((p) => p.id === currentWorkflow);

  return (
    <section aria-label="Design workflow">
      <h2 className="m-0 mb-3 text-balance text-base font-semibold tracking-tight text-[var(--text-ink,#171717)] sm:text-lg">
        Design workflow
      </h2>
      <div className="overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:thin]">
        <div
          className="inline-grid min-w-full grid-flow-col auto-cols-[minmax(200px,1fr)] rounded-xl border border-black/[0.08] bg-white "
          role="list"
        >
          {PROCESS_DEFINITIONS.map((process, index) => {
            const firstStep = process.steps[0]?.id;
            const accessible = firstStep ? canAccessStepGlobally(firstStep, stepCompletion) : false;
            const completed = isProcessComplete(process.id, stepCompletion);
            const active = process.id === currentWorkflow || index === activeProcessIndex;
            const doneCount = process.steps.filter((s) => stepCompletion[s.id]).length;
            const total = process.steps.length;

            const columnBorder =
              index < PROCESS_DEFINITIONS.length - 1 ? 'border-r border-black/[0.08]' : '';

            return (
              <article
                key={process.id}
                role="listitem"
                className={[
                  'flex flex-col px-4 py-4',
                  columnBorder,
                  active && !completed ? 'bg-[#fafafa]/60' : 'bg-white',
                  !accessible ? 'opacity-75' : '',
                ].join(' ')}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={[
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums',
                      completed
                        ? 'bg-emerald-600 text-white'
                        : active
                          ? 'bg-[var(--odp-purple)] text-white'
                          : 'border border-black/[0.1] bg-[#fafafa] text-[var(--text-secondary)]',
                    ].join(' ')}
                    aria-hidden
                  >
                    {completed ? '✓' : index + 1}
                  </span>
                  <h3 className="m-0 text-sm font-semibold tracking-tight text-[var(--text-primary)]">
                    {process.label}
                  </h3>
                </div>
                <p className="mt-2 mb-0 line-clamp-2 text-xs leading-snug text-[var(--text-secondary)]">
                  {process.description}
                </p>
                <p className="mt-2.5 mb-0 text-[11px] text-[var(--text-secondary)]">
                  <span className="font-medium tabular-nums text-[var(--text-primary)]">{doneCount}</span>
                  <span className="text-neutral-400"> / </span>
                  <span className="tabular-nums">{total}</span>
                  <span className="ml-1">steps</span>
                </p>
              </article>
            );
          })}
        </div>
      </div>
      <p className="mt-2 mb-0 text-center text-[11px] text-neutral-400 lg:hidden">
        Swipe horizontally to see all processes
      </p>
    </section>
  );
};

export default DashboardWorkflowOverview;
